/**
 * The workspace: one versioned object that every screen reads and writes.
 *
 * Every figure in the profile is a Fact — the value plus where it came from.
 * That provenance is what lets the action plan say "from your 2024 P&L, page 2",
 * lets the scenario builder refuse to run coverage on unconfirmed numbers, and
 * lets conflicting figures be shown side by side instead of silently resolved.
 *
 * Stored locally in the browser (see store.ts). When accounts arrive in a later
 * phase, this same object is what syncs.
 */

export const SCHEMA_VERSION = 1;

export type FactSource =
  /** Typed by the user (intake or a form). */
  | 'entered'
  /** Read from an uploaded document, not yet confirmed. */
  | 'extracted'
  /** Confirmed by the user — either a document figure or one they vouched for. */
  | 'confirmed';

export interface Evidence {
  docId: string;
  docName: string;
  page?: number;
  sheet?: string;
  snippet: string;
  period?: string;
}

export interface Fact<T> {
  value: T;
  source: FactSource;
  evidence?: Evidence;
  updatedAt: string;
}

export type Goal =
  | 'working-capital'
  | 'equipment-expansion'
  | 'buy-business'
  | 'real-estate'
  | 'refinance'
  | 'not-sure';

export type Stage = 'planning' | 'operating' | 'acquiring';

export type AgeBand = 'not-operating' | 'under-1' | '1-2' | '2-5' | 'over-5';

export type YesNoUnsure = 'yes' | 'no' | 'unsure';

export type DocKind =
  | 'pl'
  | 'tax-return'
  | 'balance-sheet'
  | 'bank-statement'
  | 'business-plan'
  | 'debt-schedule'
  | 'seller-financials'
  | 'purchase-agreement'
  | 'personal-financial-statement'
  | 'other';

export const DOC_KIND_LABELS: Record<DocKind, string> = {
  pl: 'Profit and loss statement',
  'tax-return': 'Business tax return',
  'balance-sheet': 'Balance sheet',
  'bank-statement': 'Business bank statement',
  'business-plan': 'Business plan or projections',
  'debt-schedule': 'Schedule of existing business debt',
  'seller-financials': "Seller's financial statements",
  'purchase-agreement': 'Purchase agreement or letter of intent',
  'personal-financial-statement': 'Personal financial statement',
  other: 'Other document',
};

/** The financial figures documents can supply. All annual, USD, except where noted. */
export type FinancialField =
  | 'annualRevenue'
  | 'cogs'
  | 'operatingExpenses'
  | 'operatingIncome'
  | 'netIncome'
  | 'interestExpense'
  | 'depreciation'
  | 'existingAnnualDebtService'
  | 'cash'
  | 'ownerSalary';

export const FINANCIAL_FIELD_LABELS: Record<FinancialField, string> = {
  annualRevenue: 'Annual revenue (sales)',
  cogs: 'Cost of goods sold',
  operatingExpenses: 'Operating expenses',
  operatingIncome: 'Operating income (before loan payments and taxes)',
  netIncome: 'Net income',
  interestExpense: 'Interest expense',
  depreciation: 'Depreciation and amortisation',
  existingAnnualDebtService: 'Existing loan payments (per year)',
  cash: 'Cash on hand',
  ownerSalary: "Owner's or officers' compensation",
};

export interface Profile {
  goal?: Fact<Goal>;
  stage?: Fact<Stage>;
  /** NAICS sector (2 digits). */
  industry?: Fact<string>;
  ageBand?: Fact<AgeBand>;
  amount?: Fact<number>;
  termMonths?: Fact<number>;
  fundsDescription?: Fact<string>;
  jobs?: Fact<number>;
  purchasePrice?: Fact<number>;
  /** Owner's own cash going in (equity injection). */
  ownerInjection?: Fact<number>;
  refinanceMonthlyPayment?: Fact<number>;
  sellerFinancialsAvailable?: Fact<YesNoUnsure>;
  occupancyMajority?: Fact<YesNoUnsure>;
  hasProjections?: Fact<YesNoUnsure>;
  /** Extra monthly operating profit the owner expects the loan to produce. */
  expectedMonthlyUplift?: Fact<number>;
  annualRevenue?: Fact<number>;
  cogs?: Fact<number>;
  operatingExpenses?: Fact<number>;
  operatingIncome?: Fact<number>;
  netIncome?: Fact<number>;
  interestExpense?: Fact<number>;
  depreciation?: Fact<number>;
  existingAnnualDebtService?: Fact<number>;
  cash?: Fact<number>;
  /** Officer compensation or guaranteed payments, as deducted on the return. */
  ownerSalary?: Fact<number>;
}

export type ProfileKey = keyof Profile;

export interface ChatMessage {
  id: string;
  role: 'assistant' | 'user';
  text: string;
  /** Intake node this message belongs to, so an answer can be edited later. */
  nodeId?: string;
  at: string;
}

export type CandidateState = 'pending' | 'confirmed' | 'rejected';

/** A figure found in a document, awaiting the user's decision. */
export interface Candidate {
  id: string;
  field: FinancialField;
  value: number;
  /** The label text that matched, e.g. "Total Revenue". */
  label: string;
  snippet: string;
  page?: number;
  sheet?: string;
  period?: string;
  state: CandidateState;
}

export type DocStatus = 'reading' | 'ready' | 'no-text' | 'unsupported' | 'error';

export interface DocumentRecord {
  id: string;
  name: string;
  mime: string;
  size: number;
  kind: DocKind;
  addedAt: string;
  status: DocStatus;
  statusDetail?: string;
  pageCount?: number;
  candidates: Candidate[];
}

export interface Scenario {
  id: string;
  name: string;
  amount: number;
  termMonths: number;
  ratePct: number;
}

export type ReadinessStatus = 'not-started' | 'needs-review' | 'ready';

export interface ChecklistOverride {
  status?: ReadinessStatus;
  note?: string;
  updatedAt: string;
}

/** A dated copy of the financial figures, taken when the owner updates them. */
export interface Snapshot {
  id: string;
  at: string;
  label: string;
  figures: Partial<Record<FinancialField | 'amount', number>>;
}

export interface Workspace {
  schemaVersion: number;
  id: string;
  createdAt: string;
  updatedAt: string;
  profile: Profile;
  intake: {
    answered: string[];
    skipped: string[];
    transcript: ChatMessage[];
  };
  /** Documents the owner said they already have (from intake). */
  docsOnHand: DocKind[];
  documents: DocumentRecord[];
  /**
   * When two sources disagree on a figure, the owner's choice: the candidate id
   * they picked, or 'profile' for the figure already in the profile.
   */
  resolutions: Partial<Record<FinancialField, { chosen: string; at: string }>>;
  scenarios: Scenario[];
  checklist: Record<string, ChecklistOverride>;
  snapshots: Snapshot[];
  /** Whether the owner has acknowledged the estimate is not a credit decision. */
  acknowledgedDisclaimer: boolean;
}

export function newId(prefix = ''): string {
  const rand = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return prefix ? `${prefix}_${rand}` : rand;
}

export function nowIso(): string {
  return new Date().toISOString();
}

export function emptyWorkspace(): Workspace {
  const t = nowIso();
  return {
    schemaVersion: SCHEMA_VERSION,
    id: newId('ws'),
    createdAt: t,
    updatedAt: t,
    profile: {},
    intake: { answered: [], skipped: [], transcript: [] },
    docsOnHand: [],
    documents: [],
    resolutions: {},
    scenarios: [],
    checklist: {},
    snapshots: [],
    acknowledgedDisclaimer: false,
  };
}

export function fact<T>(value: T, source: FactSource = 'entered', evidence?: Evidence): Fact<T> {
  return { value, source, ...(evidence ? { evidence } : {}), updatedAt: nowIso() };
}

/** True when a workspace has anything worth calling "in progress". */
export function hasProgress(ws: Workspace): boolean {
  return ws.intake.answered.length > 0 || ws.documents.length > 0 || ws.scenarios.length > 0;
}
